import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import {
  ListAlt as ListIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Assignment as AssignIcon,
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import adminTicketService from '../../services/adminTicketService';
import { Ticket, TicketStatus, TicketPriority, TicketCategory, TicketFilters } from '../../types/ticket';
import { ResponsiveTable, CopyableText } from '../../components/common';
import type { Column } from '../../components/common';
import './TicketStyles.css';

const AdminTicketList: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<TicketFilters>({});
  const [adminUsers, setAdminUsers] = useState<Array<{ _id: string; name: string; email: string }>>([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loadTickets = useCallback(async (filterParams: TicketFilters = filters) => {
    try {
      setLoading(true);
      const response = await adminTicketService.getTickets(filterParams);

      if (response && response.data && Array.isArray(response.data.tickets)) {
        setTickets(response.data.tickets);
        setTotalPages(response.data.totalPages || 1);
        setTotal(response.data.totalCount || 0);
        setCurrentPage(response.data.currentPage || 1);
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadAdminUsers = async () => {
    try {
      const users = await adminTicketService.getAdminUsers();
      setAdminUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      console.error('Error loading admin users:', error);
      setAdminUsers([]);
    }
  };

  // Get active filter label for display
  const getActiveFilterLabel = () => {
    if (searchParams.get('unassigned') === 'true') return 'تخصیص نیافته';
    if (searchParams.get('assigned_to_me') === 'true') return 'تیکت‌های من';
    if (searchParams.get('today') === 'true') return 'تیکت‌های امروز';
    if (searchParams.get('resolved_this_week') === 'true') return 'حل شده این هفته';
    if (searchParams.get('priority') === 'URGENT') return 'تیکت‌های فوری';

    // Show assignedTo filter with agent name if available
    const assignedToId = searchParams.get('assignedTo');
    if (assignedToId) {
      const agent = adminUsers.find(u => u._id === assignedToId);
      const statusParam = searchParams.get('status');
      let label = `تیکت‌های ${agent?.name || 'پشتیبان'}`;
      if (statusParam === 'RESOLVED') {
        label += ' (حل شده)';
      } else if (statusParam === 'OPEN,IN_PROGRESS') {
        label += ' (در انتظار)';
      }
      return label;
    }

    if (searchParams.get('status')) {
      const statusLabels: Record<string, string> = {
        'OPEN': 'باز',
        'IN_PROGRESS': 'در حال بررسی',
        'RESOLVED': 'حل شده',
        'CLOSED': 'بسته'
      };
      return statusLabels[searchParams.get('status') || ''] || '';
    }
    return '';
  };

  useEffect(() => {
    const initialFilters: TicketFilters & {
      unassigned?: boolean;
      assignedToMe?: boolean;
      today?: boolean;
      resolvedThisWeek?: boolean;
    } = {
      status: searchParams.get('status') as TicketStatus || undefined,
      priority: searchParams.get('priority') as TicketPriority || undefined,
      category: searchParams.get('category') as TicketCategory || undefined,
      search: searchParams.get('search') || undefined,
      assignedTo: searchParams.get('assignedTo') || undefined,
      page: 1
    };

    // Handle special filters from dashboard
    if (searchParams.get('unassigned') === 'true') {
      (initialFilters as any).unassigned = true;
    }
    if (searchParams.get('assigned_to_me') === 'true') {
      (initialFilters as any).assignedToMe = true;
    }
    if (searchParams.get('today') === 'true') {
      (initialFilters as any).today = true;
    }
    if (searchParams.get('resolved_this_week') === 'true') {
      (initialFilters as any).resolvedThisWeek = true;
      initialFilters.status = TicketStatus.RESOLVED;
    }

    // Handle comma-separated status values (e.g., OPEN,IN_PROGRESS)
    const statusParam = searchParams.get('status');
    if (statusParam && statusParam.includes(',')) {
      // Multiple statuses - will be handled by backend
      (initialFilters as any).status = statusParam;
    }

    setFilters(initialFilters);
    // Show filters when there are any URL params
    if (searchParams.toString()) {
      setShowFilters(true);
    }
    loadTickets(initialFilters);
    loadAdminUsers();
  }, [searchParams]);

  const handleFilterChange = (key: keyof TicketFilters, value: any) => {
    const newFilters = { ...filters, [key]: value || undefined, page: 1 };
    setFilters(newFilters);
    loadTickets(newFilters);
  };

  const handlePageChange = (page: number) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadTickets(newFilters);
  };

  const handleAssignTicket = async () => {
    if (!selectedTicket || !selectedAssignee) return;

    try {
      await adminTicketService.assignTicket(selectedTicket._id, selectedAssignee);
      setShowAssignDialog(false);
      setSelectedTicket(null);
      setSelectedAssignee('');
      loadTickets();
    } catch (error) {
      console.error('Error assigning ticket:', error);
    }
  };

  const handleSearch = () => {
    handleFilterChange('search', searchQuery || undefined);
  };

  const handleReset = () => {
    setFilters({ page: 1 });
    setSearchQuery('');
    loadTickets({ page: 1 });
  };

  const getStatusBadge = (status: TicketStatus) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      [TicketStatus.OPEN]: { label: 'باز', className: 'open' },
      [TicketStatus.IN_PROGRESS]: { label: 'در حال بررسی', className: 'in-progress' },
      [TicketStatus.RESOLVED]: { label: 'حل شده', className: 'resolved' },
      [TicketStatus.CLOSED]: { label: 'بسته', className: 'closed' }
    };
    const s = statusMap[status] || { label: status, className: 'closed' };
    return <span className={`status-badge ${s.className}`}>{s.label}</span>;
  };

  const getPriorityBadge = (priority: TicketPriority) => {
    const priorityMap: Record<string, { label: string; className: string }> = {
      [TicketPriority.LOW]: { label: 'کم', className: 'low' },
      [TicketPriority.MEDIUM]: { label: 'متوسط', className: 'medium' },
      [TicketPriority.HIGH]: { label: 'بالا', className: 'high' },
      [TicketPriority.URGENT]: { label: 'فوری', className: 'urgent' }
    };
    const p = priorityMap[priority] || { label: priority, className: 'low' };
    return <span className={`priority-badge ${p.className}`}>{p.label}</span>;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  // Derived page size from backend pagination (the list never sends a `limit`,
  // so we infer it to wire ResponsiveTable's pagination without changing the request).
  const rowsPerPage = totalPages > 1
    ? Math.max(1, Math.ceil(total / totalPages))
    : Math.max(1, tickets.length || 1);

  // ResponsiveTable columns — each `format` returns the same cell JSX as before.
  const columns: Column<Ticket>[] = [
    {
      id: 'ticketNumber',
      label: 'شماره تیکت',
      priority: 1,
      format: (_v, t) =>
        t.ticketNumber ? (
          <CopyableText value={t.ticketNumber} mono truncate maxWidth={160} />
        ) : (
          '-'
        ),
    },
    {
      id: 'subject',
      label: 'عنوان',
      priority: 1,
      format: (_v, t) => (
        <span
          style={{
            display: 'inline-block',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
        >
          {t.subject || '-'}
        </span>
      ),
    },
    {
      id: 'userId',
      label: 'کاربر',
      priority: 2,
      detail: true,
      format: (_v, t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 500 }}>{t.userId?.name || '-'}</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-disabled)' }}>{t.userId?.email || ''}</span>
        </div>
      ),
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 1,
      format: (_v, t) => (t.status ? getStatusBadge(t.status) : '-'),
    },
    {
      id: 'priority',
      label: 'اولویت',
      priority: 1,
      format: (_v, t) => (t.priority ? getPriorityBadge(t.priority) : '-'),
    },
    {
      id: 'assignedTo',
      label: 'مسئول',
      priority: 2,
      format: (_v, t) => (
        <span
          style={{ color: t.assignedTo?.name ? 'var(--color-text-primary)' : 'var(--color-text-disabled)' }}
        >
          {t.assignedTo?.name || 'تخصیص نیافته'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      label: 'تاریخ ایجاد',
      priority: 3,
      detail: true,
      format: (_v, t) => (
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>
          {t.createdAt ? formatDate(t.createdAt) : '-'}
        </span>
      ),
    },
  ];

  const renderActions = (ticket: Ticket) => (
    <>
      <Tooltip title="مشاهده جزئیات">
        <IconButton size="small" color="primary" onClick={() => navigate(`/tickets/${ticket._id}`)}>
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="تخصیص تیکت">
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            setSelectedTicket(ticket);
            setShowAssignDialog(true);
          }}
        >
          <AssignIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <>
      <Helmet>
        <title>لیست تیکت‌ها</title>
      </Helmet>

      <div className="ticket-page">
        {/* Header */}
        <div className="ticket-header">
          <div className="ticket-header-title">
            <h1>
              <ListIcon />
              لیست تیکت‌ها
            </h1>
            <span className="ticket-header-subtitle">
              مشاهده و مدیریت تمام تیکت‌های پشتیبانی
            </span>
          </div>

          <div className="header-actions">
            <button
              className="header-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <CollapseIcon style={{ fontSize: 18 }} /> : <ExpandIcon style={{ fontSize: 18 }} />}
              فیلترها
            </button>
            <button className="header-btn" onClick={() => loadTickets()} disabled={loading}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="filters-section">
            <div className="filters-header">
              <div className="filters-title">
                <FilterIcon />
                فیلترها و جستجو
              </div>
            </div>

            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">جستجو</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="شماره، موضوع..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>

              <div className="filter-group">
                <label className="filter-label">وضعیت</label>
                <select
                  className="filter-select"
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">همه</option>
                  <option value={TicketStatus.OPEN}>باز</option>
                  <option value={TicketStatus.IN_PROGRESS}>در حال بررسی</option>
                  <option value={TicketStatus.RESOLVED}>حل شده</option>
                  <option value={TicketStatus.CLOSED}>بسته</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">اولویت</label>
                <select
                  className="filter-select"
                  value={filters.priority || ''}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  <option value="">همه</option>
                  <option value={TicketPriority.LOW}>کم</option>
                  <option value={TicketPriority.MEDIUM}>متوسط</option>
                  <option value={TicketPriority.HIGH}>بالا</option>
                  <option value={TicketPriority.URGENT}>فوری</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">دسته‌بندی</label>
                <select
                  className="filter-select"
                  value={filters.category || ''}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">همه</option>
                  <option value={TicketCategory.TECHNICAL_SUPPORT}>پشتیبانی فنی</option>
                  <option value={TicketCategory.FINANCIAL}>مالی</option>
                  <option value={TicketCategory.GENERAL_INQUIRY}>سوال عمومی</option>
                  <option value={TicketCategory.BUG_REPORT}>گزارش باگ</option>
                  <option value={TicketCategory.FEATURE_REQUEST}>درخواست قابلیت</option>
                </select>
              </div>

              <div className="filter-group" style={{ alignSelf: 'flex-end' }}>
                <button className="filter-btn primary" onClick={handleSearch}>
                  <SearchIcon style={{ fontSize: 16 }} />
                  جستجو
                </button>
              </div>
            </div>

            <div className="filters-actions">
              <button className="filter-btn secondary" onClick={handleReset}>
                <ClearIcon style={{ fontSize: 16 }} />
                پاک کردن فیلترها
              </button>
            </div>
          </div>
        )}

        {/* Active Filter Badge */}
        {getActiveFilterLabel() && (
          <div className="active-filter-banner">
            <span className="active-filter-label">
              <FilterIcon style={{ fontSize: 16 }} />
              فیلتر فعال: <strong>{getActiveFilterLabel()}</strong>
            </span>
            <button
              className="clear-filter-btn"
              onClick={() => navigate('/tickets/list')}
            >
              <ClearIcon style={{ fontSize: 16 }} />
              حذف فیلتر
            </button>
          </div>
        )}

        {/* Table Section */}
        <div className="table-section">
          <div className="table-header">
            <span className="table-title">تیکت‌ها</span>
            <span className="table-count">{total} تیکت</span>
          </div>

          <ResponsiveTable<Ticket>
            columns={columns}
            data={tickets}
            keyField="_id"
            loading={loading}
            emptyMessage="تیکتی یافت نشد"
            renderActions={renderActions}
            pagination={totalPages > 1}
            page={currentPage - 1}
            rowsPerPage={rowsPerPage}
            totalCount={total}
            rowsPerPageOptions={[rowsPerPage]}
            onPageChange={(p) => handlePageChange(p + 1)}
          />
        </div>

        {/* Assign Dialog */}
        {showAssignDialog && (
          <div className="dialog-overlay" onClick={() => setShowAssignDialog(false)}>
            <div className="dialog" onClick={(e) => e.stopPropagation()}>
              <div className="dialog-header">
                <h2>
                  <AssignIcon />
                  تخصیص تیکت
                </h2>
                <button className="dialog-close" onClick={() => setShowAssignDialog(false)}>
                  <CloseIcon />
                </button>
              </div>
              <div className="dialog-content">
                <div className="form-group">
                  <label className="form-label">تیکت</label>
                  <div style={{ padding: '12px', background: 'var(--color-bg-default)', borderRadius: 8, color: 'var(--color-text-secondary)' }}>
                    {selectedTicket?.subject || 'نامشخص'}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">انتخاب مسئول</label>
                  <select
                    className="form-select"
                    value={selectedAssignee}
                    onChange={(e) => setSelectedAssignee(e.target.value)}
                  >
                    <option value="">انتخاب کنید...</option>
                    {adminUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="dialog-actions">
                <button className="header-btn" onClick={() => setShowAssignDialog(false)}>
                  انصراف
                </button>
                <button
                  className="header-btn primary"
                  onClick={handleAssignTicket}
                  disabled={!selectedAssignee}
                >
                  تخصیص
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminTicketList;
